if (!customElements.get('product-engraving')) {
  customElements.define(
    'product-engraving',
    class ProductEngraving extends HTMLElement {
      static VALID_TEXT_REGEX =
        /^[a-zA-Z0-9áéíóúÁÉÍÓÚàèìòùÀÈÌÒÙâêîôûÂÊÎÔÛäëïöüÄËÏÖÜñÑçÇãõÃÕßœŒæÆ&.\-'\/*: ]*$/;

      constructor() {
        super();

        this.formId = this.dataset.productFormId;
        this.forms = Array.from(document.querySelectorAll(`form[id="${this.formId}"]`));

        this.toggleInputs = Array.from(this.querySelectorAll('[data-engraving-toggle]'));
        this.fieldsWrapper = this.querySelector('[data-engraving-fields]');
        this.textInput = this.querySelector('[data-engraving-text]');
        this.charCount = this.querySelector('[data-engraving-char-count]');
        this.charLimit = this.querySelector('[data-engraving-char-limit]');
        this.counterWrapper = this.querySelector('[data-engraving-counter]');
        this.fontInputs = Array.from(this.querySelectorAll('[data-engraving-font]'));
        this.errorMessage = this.querySelector('[data-engraving-error]');

        this.onToggleChange = this.onToggleChange.bind(this);
        this.onTextInput = this.onTextInput.bind(this);
        this.onFormSubmit = this.onFormSubmit.bind(this);

        this.updateFieldsState();
      }

      connectedCallback() {
        this.toggleInputs.forEach((input) => input.addEventListener('change', this.onToggleChange));
        if (this.textInput) this.textInput.addEventListener('input', this.onTextInput);

        // Capture phase on document: guarantees this runs before the submit
        // handlers bound directly on the form (product-form.js), regardless
        // of script load order, so an invalid engraving reliably blocks the
        // add-to-cart request instead of racing it.
        document.addEventListener('submit', this.onFormSubmit, true);
      }

      disconnectedCallback() {
        this.toggleInputs.forEach((input) => input.removeEventListener('change', this.onToggleChange));
        if (this.textInput) this.textInput.removeEventListener('input', this.onTextInput);
        document.removeEventListener('submit', this.onFormSubmit, true);
      }

      isEnabled() {
        return this.toggleInputs.some(
          (input) => input.checked && input.dataset.engravingValue === 'enabled'
        );
      }

      onToggleChange() {
        this.clearError();
        this.updateFieldsState();
      }

      onTextInput(event) {
        this.clearError();
        this.updateCharCount(event.target.value.length);
      }

      updateCharCount(count) {
        if (this.charCount) this.charCount.textContent = count;

        if (this.counterWrapper && this.charLimit) {
          const limit = parseInt(this.charLimit.textContent, 10) || 0;
          this.counterWrapper.classList.toggle('product-engraving__text-counter--limit', count >= limit && limit > 0);
        }
      }

      updateFieldsState() {
        const enabled = this.isEnabled();

        if (this.fieldsWrapper) this.fieldsWrapper.hidden = !enabled;

        if (this.textInput) {
          this.textInput.disabled = !enabled;
          if (!enabled) {
            this.textInput.value = '';
            this.updateCharCount(0);
          }
        }

        this.fontInputs.forEach((input, index) => {
          input.disabled = !enabled;
          if (!enabled) input.checked = index === 0;
        });
      }

      onFormSubmit(event) {
        if (!this.forms.includes(event.target)) return;
        if (!this.isEnabled()) return;

        const text = this.textInput ? this.textInput.value.trim() : '';

        if (!text) {
          this.blockSubmit(event, this.dataset.errorEmpty);
          return;
        }

        if (!ProductEngraving.VALID_TEXT_REGEX.test(text)) {
          this.blockSubmit(event, this.dataset.errorInvalidChars);
        }
      }

      blockSubmit(event, message) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError(message);
        if (this.textInput) {
          this.textInput.classList.add('product-engraving__text-input--error');
          // The submit can come from the sticky bar or the action bar, whose buttons
          // live far from this block. Bring the field into view so the shopper sees
          // why the form did not submit instead of facing a dead button.
          this.textInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          this.textInput.focus({ preventScroll: true });
        }
      }

      showError(message) {
        if (!this.errorMessage || !message) return;
        this.errorMessage.textContent = message;
        this.errorMessage.hidden = false;
      }

      clearError() {
        if (!this.errorMessage) return;
        this.errorMessage.hidden = true;
        this.errorMessage.textContent = '';
        if (this.textInput) this.textInput.classList.remove('product-engraving__text-input--error');
      }
    }
  );
}
